import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
    DateUtilPipe,
    GoogleTagManagerService,
    MAT_ICON,
    TOAST_STATE,
    ToastmessageService,
    TranslationPipe,
    TranslationService
} from '@prekog/common-ui';
import { Observable } from 'rxjs';
import { Category } from '../shared/category.model';
import { Material } from '../shared/material.model';
import { PublicationService } from '../shared/publication.service';
import { SiteConfiguration } from '../shared/site-configuration.model';
import { GetSiteConfigurationByKeyUseCase } from '../use-cases/getsiteconfigurationbykey/get-site-configuration-by-key.usecase';
import { GetCategoryProductsRequest } from './model/get-category-products.request';
import { GetCategoryProductsResponse } from './model/get-category-products.response';
import { GetCategoryProductsUseCase } from './use-cases/get-category-products.usecase';

@Component({
    selector: 'app-booksbycategory',
    templateUrl: './booksbycategory.component.html',
    styleUrls: ['./booksbycategory.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatFormFieldModule,
        MatSelectModule,
        TranslationPipe,
        RouterModule,
        DateUtilPipe,
        FormsModule
    ]
})
export class BooksByCategoryComponent implements OnInit {
    category?: Category;
    filteredPublication: Material[] = [];
    orderableProducts: Material[] = [];
    offerLines = signal<string[]>([]);
    offerLinesActive = signal<boolean>(false);
    router: Router = inject(Router);

    private _materialsHome: Material[] = [];
    private _preorderMaterials: Material[] = [];
    private _listFilter = '';
    private _getCategoryProductsUseCase = inject(GetCategoryProductsUseCase);
    private _googleTagManagerService = inject(GoogleTagManagerService);
    private _publicationService = inject(PublicationService);
    private _toastMessageService = inject(ToastmessageService);
    private _translateService = inject(TranslationService);
    private _route = inject(ActivatedRoute);
    private _getOfferLinesUseCase = inject(GetSiteConfigurationByKeyUseCase);

    get listFilter(): string {
        return this._listFilter;
    }

    set listFilter(value: string) {
        this._listFilter = value;
        this.filteredPublication = this.performFilter(value);
    }

    ngOnInit(): void {
        this._route.params.subscribe((params: Record<string, string>) => {

            const rawParam = params['id'];
            const decoded = decodeURIComponent(rawParam);
            const id = Number(decoded.split('/').pop()?.split('?')[0] || '');
            const getCategoryProductsRequest: GetCategoryProductsRequest = {
                categoryId: id
            };

            (this._getCategoryProductsUseCase.execute(getCategoryProductsRequest) as Observable<GetCategoryProductsResponse>)
                .subscribe((response: GetCategoryProductsResponse) => {
                    this.orderableProducts = response.categoryProducts as Material[];
                    this.category = response.category;
                    if (this.orderableProducts.length === 0) {
                        this.router.navigateByUrl('/');
                        this._toastMessageService.displayToastMessage(
                            TOAST_STATE.error,
                            this._translateService.translate('booksByCategory.noCategoryBook'),
                            MAT_ICON.error
                        );
                        this._dismissError();
                    } else {
                        this._initializeProducts();
                        this.filteredPublication = this.orderableProducts;
                        this._googleTagManagerService.viewProductItemList(this.orderableProducts);
                        this._getOfferLines(id);
                    }
                });
        });
    }

    private _getOfferLines(categoryId: number): void {
        if (!categoryId) {
            return;
        }
        const key = 'category-' + categoryId + '-lines';
        (this._getOfferLinesUseCase.execute({ configurationKey: key }) as Observable<SiteConfiguration>).subscribe(
            (config) => {
                this.offerLinesActive.set(config.active ?? false);
                this.offerLines.set(config.configurationValue ? config.configurationValue.split('\n') : []);
            }
        );
    }

    private _initializeProducts(): void {
        this.orderableProducts.forEach((product, index) => {
            if (product.hasPreorder) {
                product.oldPrice = product.originalPrice;
                product.originalPrice = product.preorderPrice;
                product.typeOfPrice = this._translateService.translate('price.typeOfPrice.preorder');
                this._preorderMaterials.push(product);
            }
            if (product.isAvailable) {
                if (product.webshopPriceActive) {
                    product.oldPrice = product.originalPrice;
                    product.originalPrice = product.webshopPrice;
                    if (product.discountLimitedUntil === null) {
                        product.typeOfPrice = this._translateService.translate('price.typeOfPrice.webshop');
                    } else {
                        product.typeOfPrice = this._translateService.translate('price.typeOfPrice.fixedPrice');
                    }
                } else if (product.discountPriceActive) {
                    product.oldPrice = product.originalPrice;
                    product.originalPrice = product.discountPrice;
                    product.typeOfPrice = this._translateService.translate('price.typeOfPrice.discount');
                } else {
                    if (product.discountLimitedUntil === null) {
                        product.typeOfPrice = this._translateService.translate('price.typeOfPrice.default');
                    } else {
                        product.typeOfPrice = this._translateService.translate('price.typeOfPrice.fixedPrice');
                    }
                }
            }
            if (index < 4) {
                this._materialsHome.push(product);
            }
        });
    }

    private _dismissError(): void {
        setTimeout(() => {
            this._toastMessageService.dismissToast();
        }, 3000);
    }

    performFilter(filterBy: string): Material[] {
        filterBy = filterBy.toLocaleLowerCase();
        return this.orderableProducts.filter(
            (publication: Material) =>
                publication.title?.toLocaleLowerCase().includes(filterBy) || publication.author?.toLocaleLowerCase().includes(filterBy)
        );
    }

    addToCart(material: Material): void {
        if (!sessionStorage.getItem('token') && material.hasPreorder) {
            sessionStorage.setItem('material', JSON.stringify(material));
            this._publicationService.openLogin();
        } else {
            this._publicationService.addToCart(material);
        }
    }

    orderbyAscPrice(): void {
        this.filteredPublication = this.filteredPublication.sort((a, b) => a.originalPrice! - b.originalPrice!);
    }

    orderbyDescPrice(): void {
        this.filteredPublication = this.filteredPublication.sort((a, b) => b.originalPrice! - a.originalPrice!);
    }

    orderbyTitleAbc(): void {
        this.filteredPublication = this.filteredPublication.sort((a, b) => a.title!.localeCompare(b.title!));
    }

    orderByNewest(): void {
        this.filteredPublication = this.filteredPublication.sort((a, b) => b.id! - a.id!);
    }

    showAllBooks(): void {
        this.filteredPublication = JSON.parse(sessionStorage.getItem('products') as string) as Material[];
    }
}
